/**
 * HTML 콘텐츠에서 <style> 태그를 찾아서 특정 컨테이너 내에서만 적용되도록 CSS 스코핑을 수행한다.
 */
export function applyCSSScoping(htmlContent: string, scopeSelector: string): string {
  if (!htmlContent) return htmlContent;

  const styleTagRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;

  return htmlContent.replace(styleTagRegex, (match, cssContent) => {
    const scopedCSS = scopeCSSRules(cssContent, scopeSelector);
    return `<style>${scopedCSS}</style>`;
  });
}

/**
 * CSS 규칙들을 특정 스코프 내에서만 적용되도록 변환한다.
 * 브라켓 깊이 추적 방식으로 @keyframes, @media 등 중첩 블록을 올바르게 처리한다.
 */
function scopeCSSRules(cssContent: string, scopeSelector: string): string {
  if (!cssContent) return cssContent;

  // CSS 주석 제거
  const css = cssContent.replace(/\/\*[\s\S]*?\*\//g, '');

  const result: string[] = [];
  let i = 0;

  while (i < css.length) {
    // 공백 건너뜀
    if (/\s/.test(css[i])) {
      result.push(css[i]);
      i++;
      continue;
    }

    // @ 규칙 처리
    if (css[i] === '@') {
      const atRuleMatch = css.slice(i).match(/^(@[\w-]+)/);
      const atRuleName = atRuleMatch ? atRuleMatch[1].toLowerCase() : '';

      if (atRuleName === '@keyframes' || atRuleName === '@-webkit-keyframes') {
        // @keyframes: 선택자 스코핑 없이 블록 전체를 그대로 유지
        const blockStart = css.indexOf('{', i);
        if (blockStart === -1) break;
        let depth = 0;
        let j = blockStart;
        while (j < css.length) {
          if (css[j] === '{') depth++;
          else if (css[j] === '}') {
            depth--;
            if (depth === 0) {
              result.push(css.slice(i, j + 1));
              i = j + 1;
              break;
            }
          }
          j++;
        }
        if (i !== j + 1) break; // 블록 끝을 못 찾은 경우
      } else if (atRuleName === '@media' || atRuleName === '@supports') {
        // @media / @supports: 내부 규칙들만 스코핑, @media 래퍼는 유지
        const blockStart = css.indexOf('{', i);
        if (blockStart === -1) break;
        const atHead = css.slice(i, blockStart + 1); // "@media (...) {"

        let depth = 1;
        let j = blockStart + 1;
        while (j < css.length && depth > 0) {
          if (css[j] === '{') depth++;
          else if (css[j] === '}') depth--;
          j++;
        }

        const innerCSS = css.slice(blockStart + 1, j - 1);
        const scopedInner = scopeCSSRules(innerCSS, scopeSelector);
        result.push(`${atHead}${scopedInner}}`);
        i = j;
      } else if (atRuleName === '@import' || atRuleName === '@charset') {
        // 세미콜론으로 끝나는 단순 at-rule
        const end = css.indexOf(';', i);
        if (end === -1) break;
        result.push(css.slice(i, end + 1));
        i = end + 1;
      } else if (atRuleName === '@font-face') {
        // @font-face: 그대로 유지
        const blockStart = css.indexOf('{', i);
        if (blockStart === -1) break;
        let depth = 0;
        let j = blockStart;
        while (j < css.length) {
          if (css[j] === '{') depth++;
          else if (css[j] === '}') {
            depth--;
            if (depth === 0) {
              result.push(css.slice(i, j + 1));
              i = j + 1;
              break;
            }
          }
          j++;
        }
      } else {
        // 기타 at-rule: 블록이 있으면 블록 전체, 없으면 세미콜론까지
        const blockStart = css.indexOf('{', i);
        const semiEnd = css.indexOf(';', i);
        if (blockStart === -1 || (semiEnd !== -1 && semiEnd < blockStart)) {
          const end = semiEnd !== -1 ? semiEnd + 1 : css.length;
          result.push(css.slice(i, end));
          i = end;
        } else {
          let depth = 0;
          let j = blockStart;
          while (j < css.length) {
            if (css[j] === '{') depth++;
            else if (css[j] === '}') {
              depth--;
              if (depth === 0) {
                result.push(css.slice(i, j + 1));
                i = j + 1;
                break;
              }
            }
            j++;
          }
        }
      }
      continue;
    }

    // 일반 CSS 규칙: 선택자 + 선언 블록
    const braceStart = css.indexOf('{', i);
    if (braceStart === -1) break;

    const selectorStr = css.slice(i, braceStart).trim();

    // 선언 블록의 끝 탐색 (깊이 1 기준)
    let depth = 1;
    let j = braceStart + 1;
    while (j < css.length && depth > 0) {
      if (css[j] === '{') depth++;
      else if (css[j] === '}') depth--;
      j++;
    }

    const declarations = css.slice(braceStart + 1, j - 1);

    if (!selectorStr) {
      result.push(`{${declarations}}`);
      i = j;
      continue;
    }

    // 이미 스코프가 적용된 경우 그대로 유지
    if (selectorStr.includes(scopeSelector)) {
      result.push(`${selectorStr} {${declarations}}`);
      i = j;
      continue;
    }

    // 콤마로 구분된 선택자들을 각각 스코핑
    const selectors = selectorStr.split(',').map(s => s.trim()).filter(Boolean);
    const scopedSelectors = selectors.map(sel => {
      if (sel === ':root' || sel === 'html' || sel === 'body') {
        return scopeSelector;
      }
      if (sel === '*') {
        return `${scopeSelector} *`;
      }
      return `${scopeSelector} ${sel}`;
    });

    result.push(`${scopedSelectors.join(', ')} {${declarations}}`);
    i = j;
  }

  return result.join('');
}

/**
 * HTML 콘텐츠에서 <script> 태그를 제거한다.
 */
export function removeScriptTags(htmlContent: string): string {
  if (!htmlContent) return htmlContent;
  return htmlContent.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
}

/**
 * HTML 콘텐츠를 안전하게 처리한다. (CSS 스코핑 + 스크립트 제거)
 */
export function sanitizeAndScopeHTML(
  htmlContent: string,
  scopeSelector: string,
  removeScripts: boolean = true
): string {
  let processedHTML = htmlContent;

  if (removeScripts) {
    processedHTML = removeScriptTags(processedHTML);
  }

  processedHTML = applyCSSScoping(processedHTML, scopeSelector);

  return processedHTML;
}
